import Moment from 'moment';

const generateMonthRangeFilter = state => {
  if (!state.filters || !state.filters.month) {
    return null;
  }
  const dateFormat = 'YYYY-MM';
  const currentMonth = Moment(state.filters.month, dateFormat);
  const previousMonth = currentMonth.clone().subtract(1, 'month');
  const nextMonth = currentMonth.clone().add(1, 'month');
  return {
    currentMonth: currentMonth.format(dateFormat),
    previousMonth: previousMonth.format(dateFormat),
    nextMonth: nextMonth.format(dateFormat),
  };
};

const isDateInMonth = (date, monthFilters) => {
  const dayOffset = -3;
  if (!monthFilters) {
    return true;
  }
  const dateMonth = date.slice(0, dayOffset);
  return dateMonth === monthFilters.currentMonth
    || dateMonth === monthFilters.previousMonth
    || dateMonth === monthFilters.nextMonth;
};


const getInspectorAvailabilities = (availabilities, filters) => {
  // get filter
  const inspectorFilter = filters ? filters.inspector : null;

  return inspectorFilter
    ? availabilities.filter(availability => availability.legacyInspectorId === inspectorFilter)
    : availabilities;
};

const filteredDates = state => {
  let output = getInspectorAvailabilities(state.records, state.filters);
  const monthFilters = generateMonthRangeFilter(state);

  // filter by month range
  output = output.filter(availability => isDateInMonth(availability.date, monthFilters));

  // remove duplicated dates and sort resulting array
  output = [...new Set(output.map(availability => availability.date))]
    .sort((a, b) => {
      const aDatetime = new Date(a);
      const bDatetime = new Date(b);
      return aDatetime - bDatetime;
    });

  return output;
};

const prevAvailability = (state, getters) => {
  if (!state.filters || !state.filters.date) {
    return null;
  }
  const currentTime = new Date(state.filters.date).getTime();
  const availabilities = getters.filteredDates.slice(0);
  return availabilities
    .reverse()
    .find(availability => {
      const availabilityTime = new Date(availability).getTime();
      return availabilityTime < currentTime;
    });
};

const nextAvailability = (state, getters) => {
  if (!state.filters || !state.filters.date) {
    return null;
  }

  const currentTime = new Date(state.filters.date).getTime();
  const availabilities = getters.filteredDates.slice(0);
  return availabilities.find(availability => {
    const availabilityTime = new Date(availability).getTime();
    return availabilityTime > currentTime;
  });
};

const filteredTimeslots = state => {
  if (state.filters && state.filters.date) {
    let output = getInspectorAvailabilities(state.records, state.filters);

    // filter by specific date
    output = output.filter(availability => availability.date === state.filters.date);

    // remove duplicated startTimes and sort resulting array
    output = [...new Set(output.map(availability => availability.startTime))]
      .sort((a, b) => {
        const aDatetime = new Date(`1970/01/01 ${a}:00`);
        const bDatetime = new Date(`1970/01/01 ${b}:00`);
        return aDatetime - bDatetime;
      });

    return output;
  }
  return [];
};

const selectedInspector = state => ((state.filters
  && state.filters.inspector
  && state.inspectors)
  ? state.inspectors.find(inspector => inspector.legacyId === state.filters.inspector)
  : null);

const selectedAvailabilityExists = (state, getters) => {
  if (!state.filters
    || !state.filters.date
    || !state.filters.time) {
    return false;
  }
  return getters.filteredTimeslots.includes(state.filters.time);
};

const selectedTimeslot = state => {
  let foundSelectedDate;
  let foundSelectedInspector;
  let foundSelectedTime;
  if (state.filters) {
    foundSelectedDate = state.filters.date;
    foundSelectedInspector = state.filters.inspector;
    foundSelectedTime = state.filters.time;
  }

  if (!foundSelectedTime || !foundSelectedDate) {
    return null;
  }

  const convertAvailabilityToTimeslot = availability => {
    const foundInspector = state.inspectors.find(inspector => inspector
      .legacyId === availability.legacyInspectorId);

    return {
      chosenDate: availability.date,
      end: availability.endTime,
      fullName: `${foundInspector.givenName} ${foundInspector.familyName}`,
      inspectorId: availability.legacyInspectorId,
      start: availability.startTime,
      time: availability.startTime,
    };
  };

  // find availability by day, time, and inspector
  const foundAvailability = state.records.find(availability => availability
    .legacyInspectorId === foundSelectedInspector
      && availability.date === foundSelectedDate
      && availability.startTime === foundSelectedTime);

  if (foundAvailability) {
    return convertAvailabilityToTimeslot(foundAvailability);
  }

  // get random availability for day and time
  const availabilitiesForDayTime = state.records.filter(availability => availability
    .date === foundSelectedDate
      && availability.startTime === foundSelectedTime);
  const randomAvailabilityIndex = Math
    .floor(Math.random() * availabilitiesForDayTime.length); // NOSONAR
  const randomAvailability = availabilitiesForDayTime[randomAvailabilityIndex];
  if (randomAvailability) {
    return convertAvailabilityToTimeslot(randomAvailability);
  }

  return null;
};

export default {
  filteredDates,
  filteredTimeslots,
  nextAvailability,
  prevAvailability,
  selectedAvailabilityExists,
  selectedInspector,
  selectedTimeslot,
};
